/**
 * 店舗一覧にタグ種別の列を足す（FKV3-13002）
 *
 * 列名＝タグ種別名、値＝その店舗に付いているタグ名。未設定は空欄にする。
 * 商品マスタ抽出の商品グループ列と同じ見え方に揃えている。
 *
 * タグ種別・タグ名・付与はすべて画面が取得済みなので、結合はここで完結する
 * （バックエンドに店舗階層×タグの結合を持たせない）。
 */

import type { SellerHierarchy } from '@/lib/types';
import type { SellerTagAssignment, SellerTagType } from '@/lib/types/feature/seller-tag';
import { buildCsvHeaderNames } from './buildImportPlan';

/** 店舗階層の1行に、その店舗のタグを添えたもの */
export interface SellerRowWithTags {
  /** 店舗ID（内部ID）。タグの突き合わせと行キーに使う */
  sellerId: string;
  /** 店舗ID（顧客側）。画面・CSVに出すのはこちら */
  sellerCustomerId: string;
  sellerName: string;
  hierarchy: string;
  postCode: string;
  /** タグ種別ID → その店舗に付いているタグ名。未設定の種別はキーを持たない */
  tagNames: Map<string, string>;
}

/**
 * 店舗の内部ID → (タグ種別ID → タグ名) を作る。
 * 1店舗には1つのタグ種別につき1件しか付かないので、値は1つで足りる。
 */
function buildTagNameIndex(
  tagTypes: SellerTagType[],
  assignments: SellerTagAssignment[],
): Map<string, Map<string, string>> {
  // タグ名IDから表示名を引く索引（付与はIDしか持たない）
  const valueNameById = new Map<string, string>();
  for (const type of tagTypes) {
    for (const value of type.tags) {
      valueNameById.set(value.id, value.name);
    }
  }

  const bySeller = new Map<string, Map<string, string>>();
  for (const assignment of assignments) {
    const name = valueNameById.get(assignment.tag_id);
    // 削除済みのタグ名を指す付与は列に出さない（マスタ側が正）
    if (name === undefined) continue;
    const byType = bySeller.get(assignment.seller_id) ?? new Map<string, string>();
    byType.set(assignment.tag_type_id, name);
    bySeller.set(assignment.seller_id, byType);
  }
  return bySeller;
}

/** 店舗階層一覧の各行に、その店舗のタグを添える */
export function buildSellerRowsWithTags(
  hierarchies: SellerHierarchy[],
  tagTypes: SellerTagType[],
  assignments: SellerTagAssignment[],
): SellerRowWithTags[] {
  const bySeller = buildTagNameIndex(tagTypes, assignments);
  return hierarchies.map((h) => {
    const sellerId = h.seller_id ?? '';
    return {
      sellerId,
      sellerCustomerId: h.seller_customer_id ?? '',
      sellerName: h.seller_name ?? '',
      hierarchy: h.seller_hierarchy ?? '',
      postCode: h.post_code ?? '',
      tagNames: bySeller.get(sellerId) ?? new Map<string, string>(),
    };
  });
}

/**
 * CSVの列名を決める。
 *
 * タグ種別名が固定列と衝突すると `downloadCSV` は同じキーで上書きしてしまい、
 * 店舗名がタグで消えるなど黙って壊れる。衝突したときだけ接尾辞で逃がす。
 * タグ種別名どうしはアカウント内で一意（DBの制約）なので、ここでは考えなくてよい。
 *
 * 一括登録の取り込みと同じ規則を使う（`buildCsvHeaderNames`）。ずれると、
 * このCSVを貼り戻したときに該当する種別が黙って落ちる。
 */
export function buildTagCsvHeaders(tagTypes: SellerTagType[]): string[] {
  return buildCsvHeaderNames(tagTypes.map((type) => type.name));
}

/** 店舗階層一覧のCSV行を作る（店舗ID, 店舗名, 店舗階層, 郵便番号, タグ種別…） */
export function buildSellerHierarchyCsvRows(
  rows: SellerRowWithTags[],
  tagTypes: SellerTagType[],
): Record<string, string>[] {
  const headers = buildTagCsvHeaders(tagTypes);
  return rows.map((row) => {
    const record: Record<string, string> = {
      店舗ID: row.sellerCustomerId,
      店舗名: row.sellerName,
      店舗階層: row.hierarchy,
      郵便番号: row.postCode,
    };
    tagTypes.forEach((type, i) => {
      // 未設定は空欄（CSVでは何も書かない）
      record[headers[i]] = row.tagNames.get(type.id) ?? '';
    });
    return record;
  });
}
